import type { CardProps } from '@nextui-org/react';

import React, { useMemo } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Tabs,
  Tab,
  ScrollShadow,
  CardFooter,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';

import { useUserNotifications } from '@/context/user-notifications/hooks';
import { NotificationTemplate } from '@/context/user-notifications/helpers/types';
import { useAuthContext } from '@/hooks/useAuthContext';
import { NotificationItem } from '../notification-item';

enum NotificationTabs {
  All = 'all',
  Unread = 'unread',
  Read = 'read',
}

export const NotificationCardNav = (props: CardProps) => {
  const [activeTab, setActiveTab] = React.useState<NotificationTabs>(NotificationTabs.All);
  const {
    notifications,
    unreadNotifications,
    totalNotifications,
    markAllAsRead,
    fetchNextPage,
    isLoading,
  } = useUserNotifications();
  const { user } = useAuthContext();
  const notificationsMemo: Record<NotificationTabs, NotificationTemplate[]> = useMemo(
    () => ({
      [NotificationTabs.All]: notifications,
      [NotificationTabs.Read]: notifications.filter((item) => item.notification.isRead()),
      [NotificationTabs.Unread]: notifications.filter((item) => !item.notification.isRead()),
    }),
    [notifications]
  );
  const activeNotifications = notificationsMemo[activeTab];
  return (
    <Card className="w-full max-w-[420px]" {...props}>
      <CardHeader className="flex flex-col px-0 pb-0">
        <div className="flex w-full items-center justify-between px-5 py-2">
          <div className="inline-flex items-center gap-1">
            <h4 className="inline-block align-middle text-large font-medium">Notifications</h4>
            <Chip size="sm" variant="flat">
              {notifications.length}
            </Chip>
          </div>
          <Button
            className="h-8 px-3"
            color="primary"
            radius="full"
            variant="light"
            onPress={markAllAsRead}
          >
            Mark all as read
          </Button>
        </div>
        <Tabs
          aria-label="Notifications"
          classNames={{
            base: 'w-full',
            tabList: 'gap-6 px-6 py-0 w-full relative rounded-none border-b border-divider',
            cursor: 'w-full',
            tab: 'max-w-fit px-2 h-12',
          }}
          color="primary"
          selectedKey={activeTab}
          variant="underlined"
          onSelectionChange={(selected) => setActiveTab(selected as NotificationTabs)}
        >
          <Tab
            key="all"
            title={
              <div className="flex items-center space-x-2">
                <span>All</span>
                <Chip size="sm" variant="flat">
                  {totalNotifications}
                </Chip>
              </div>
            }
          />
          <Tab
            key="unread"
            title={
              <div className="flex items-center space-x-2">
                <span>Unread</span>
                <Chip size="sm" variant="flat">
                  {unreadNotifications}
                </Chip>
              </div>
            }
          />
          <Tab key="read" title="Read" />
        </Tabs>
      </CardHeader>
      <CardBody className="w-full gap-0 p-0">
        <ScrollShadow className="h-[500px] w-full">
          {activeNotifications?.length > 0 ? (
            activeNotifications.map((item) => (
              <NotificationItem
                key={item.notification.getId()}
                {...item}
                title={{
                  titlePrefix: item.title.titlePrefix,
                  titleTopic: item.title.titleTopic,
                }}
              />
            ))
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2">
              <Icon className="text-default-400" icon="solar:bell-off-linear" width={40} />
              <p className="text-small text-default-400">No notifications yet.</p>
            </div>
          )}
        </ScrollShadow>
      </CardBody>
      <CardFooter className="justify-end gap-2 px-4">
        {user?.id && notifications.length < totalNotifications && (
          <Button onPress={() => fetchNextPage(`${user.id}`, 10)} disabled={isLoading}>
            {isLoading ? 'Cargando...' : 'Cargar más'}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
